import { Box, Button, Flex, Heading, HStack, Input, Text, Textarea, VStack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Edge, Node } from '@xyflow/react'
import { XIcon } from 'lucide-react'
import { useState } from 'react'
import { type CreateFlowTemplateRequest, flowTemplatesApi } from '@/lib/api'
import { useColorModeValue } from '~/color-mode'
import { toaster } from '~/toaster'

interface SaveTemplateDialogProps {
  nodes: Node[]
  edges: Edge[]
  onClose: () => void
  onSuccess?: () => void
}

const INDUSTRY_OPTIONS = [
  'Automotive',
  'Energy',
  'Manufacturing',
  'Logistics',
  'Oil & Gas',
  'Pharmaceutical',
  'Smart Building',
  'Telecommunications',
  'Healthcare',
  'Finance',
  'Other',
]

function SaveTemplateDialog({ nodes, edges, onClose, onSuccess }: SaveTemplateDialogProps) {
  const queryClient = useQueryClient()
  const selectBorderColor = useColorModeValue('#E0DFDF', '#5F5D5A')
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [industry, setIndustry] = useState('')

  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    setName(value)
    // Generate slug: lowercase, replace spaces with hyphens, remove special chars
    const generatedSlug = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50)
    setSlug(generatedSlug)
  }

  const saveMutation = useMutation({
    mutationFn: (data: CreateFlowTemplateRequest) => flowTemplatesApi.create(data),
    onSuccess: () => {
      toaster.create({
        title: 'Template saved',
        description: `Template "${name}" has been created successfully.`,
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['flow-templates'] })
      onSuccess?.()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to save template',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSave = () => {
    if (!name.trim()) {
      toaster.create({
        title: 'Name required',
        description: 'Please enter a name for the template.',
        type: 'warning',
      })
      return
    }

    if (!slug.trim()) {
      toaster.create({
        title: 'Slug required',
        description: 'Please enter a slug for the template.',
        type: 'warning',
      })
      return
    }

    saveMutation.mutate({
      name: name.trim(),
      slug: slug.trim(),
      description: description.trim() || undefined,
      industry: industry || undefined,
      nodes: nodes as unknown[],
      edges: edges as unknown[],
      isActive: true,
    })
  }

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={2000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="500px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <Flex justify="space-between" align="center" p={4} borderBottomWidth="1px">
          <Heading size="md">Save as Template</Heading>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <XIcon size={16} />
          </Button>
        </Flex>

        {/* Content */}
        <VStack p={4} gap={4} align="stretch">
          <Box>
            <Text fontWeight="medium" mb={1}>
              Template Name *
            </Text>
            <Input
              placeholder="e.g., Smart Factory Architecture"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
            />
          </Box>

          <Box>
            <Text fontWeight="medium" mb={1}>
              Slug *
            </Text>
            <Input
              placeholder="e.g., smart-factory"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
            <Text fontSize="xs" color="content.secondary" mt={1}>
              Unique identifier used in URLs. Auto-generated from name.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium" mb={1}>
              Industry
            </Text>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '6px',
                border: `1px solid ${selectBorderColor}`,
                backgroundColor: 'white',
                fontSize: '14px',
              }}
            >
              <option value="">Select an industry...</option>
              {INDUSTRY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </Box>

          <Box>
            <Text fontWeight="medium" mb={1}>
              Description
            </Text>
            <Textarea
              placeholder="Brief description of this architecture template..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </Box>

          <Box p={3} bg="bg.subtle" borderRadius="md">
            <Text fontSize="sm" color="content.secondary">
              This will save the current playground with {nodes.length} nodes and {edges.length}{' '}
              edges as a reusable template.
            </Text>
          </Box>
        </VStack>

        {/* Footer */}
        <Flex justify="flex-end" p={4} borderTopWidth="1px" gap={2}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorPalette="brand"
            onClick={handleSave}
            loading={saveMutation.isPending}
            disabled={!name.trim() || !slug.trim()}
          >
            Save Template
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export default SaveTemplateDialog
